/** Variant fragment swapping. */

const VARIANT_CHANGE_EVENT = 'dieux:product-variant-change';
const FRAGMENTS_CHANGE_EVENT = 'dieux:variant-fragments:change';

class DieuxVariantFragments extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    document.addEventListener(VARIANT_CHANGE_EVENT, (event) => this.#handleVariantChange(event), {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Reveal the fragment for `variantId` and hide the rest.
   *
   * @param {string|number} variantId - Variant the page has switched to.
   */
  selectVariant(variantId) {
    const nextVariantId = String(variantId || '');
    const fragments = this.#ownedElements('[data-variant-fragment]');
    const nextFragment =
      fragments.find((fragment) => fragment.dataset.variantFragment === nextVariantId) || null;

    for (const fragment of fragments) {
      const isActive = fragment === nextFragment;
      fragment.hidden = !isActive;
      fragment.inert = !isActive;
    }

    const hasContent = Boolean(nextFragment);
    const emptyState = this.#ownedElements('[data-variant-fragments-empty]')[0];

    this.dataset.activeVariantId = nextVariantId;
    this.dataset.hasActiveContent = String(hasContent);
    if (emptyState) emptyState.hidden = hasContent;
    this.hidden = !hasContent && this.dataset.designMode !== 'true';

    this.dispatchEvent(
      new CustomEvent(FRAGMENTS_CHANGE_EVENT, {
        bubbles: true,
        detail: { variantId: nextVariantId, hasContent, fragment: nextFragment },
      })
    );
  }

  #handleVariantChange(event) {
    if (String(event.detail?.productId || '') !== String(this.dataset.productId || '')) return;
    this.selectVariant(event.detail?.variant?.id);
  }

  /**
   * Descendants matching `selector` that belong to this host rather than a nested one.
   *
   * @param {string} selector - Attribute selector for the fragment or placeholder contract.
   * @returns {Element[]} Owned matches, queried live so design-mode DOM patches stay covered.
   */
  #ownedElements(selector) {
    return [...this.querySelectorAll(selector)].filter(
      (element) => element.closest('dieux-variant-fragments') === this
    );
  }
}

if (!customElements.get('dieux-variant-fragments')) {
  customElements.define('dieux-variant-fragments', DieuxVariantFragments);
}
